/**
 * Pentes proposées au couvreur, de 0/12 à 18/12 sans trou.
 *
 * `value` est le facteur qui transforme la surface au sol en surface de toit :
 * √(1 + (rise/12)²). Ce n'est pas un réglage esthétique, c'est le nombre qui
 * multiplie le prix — d'où le pas de 1 sur toute la plage.
 *
 * La liste sautait de 10/12 à 12/12 et n'avait ni 1/12 ni 2/12. Un toit à
 * 11/12 mesuré par satellite retombait alors sur 6/12 : environ 18 % de
 * surface perdue, donc une soumission trop basse que le couvreur n'aurait
 * jamais rattrapée. Elle s'arrêtait aussi à 12/12 alors que le satellite monte
 * à 18/12 — le 86 rue du Midi a deux pans à 13/12.
 *
 * `rise` existe pour que la reconnaissance se fasse sur un nombre plutôt que
 * sur le libellé : « 12/12 (steep) » contient le texte « 2/12 », ce qui faisait
 * lire un toit presque plat comme le plus pentu de la liste.
 */
export const PITCH_OPTIONS = [
  { rise: 0, label: "Flat (0/12)", value: 1.0 },
  { rise: 1, label: "1/12", value: 1.003 },
  { rise: 2, label: "2/12", value: 1.014 },
  { rise: 3, label: "Low 3/12", value: 1.031 },
  { rise: 4, label: "4/12", value: 1.054 },
  { rise: 5, label: "5/12", value: 1.083 },
  { rise: 6, label: "6/12 (typical)", value: 1.118 },
  { rise: 7, label: "7/12", value: 1.158 },
  { rise: 8, label: "8/12", value: 1.202 },
  { rise: 9, label: "9/12", value: 1.25 },
  { rise: 10, label: "10/12", value: 1.302 },
  { rise: 11, label: "11/12", value: 1.357 },
  { rise: 12, label: "12/12 (steep)", value: 1.414 },
  // Au-dela de 12/12 : rare en residentiel, mais Solar en renvoie (jusqu'a
  // 18/12) et le 86 rue du Midi en a deux pans a 13/12. Sans ces entrees ils
  // etaient rabattus sur 12/12, soit 4 % de surface perdue.
  { rise: 13, label: "13/12", value: 1.474 },
  { rise: 14, label: "14/12", value: 1.537 },
  { rise: 15, label: "15/12", value: 1.601 },
  { rise: 16, label: "16/12", value: 1.667 },
  { rise: 17, label: "17/12", value: 1.734 },
  { rise: 18, label: "18/12", value: 1.803 },
];

/** Distance en mètres entre deux points (haversine). */
export function distanceM(lat1, lng1, lat2, lng2) {
  const R = 6371000;
  const r = (d) => (d * Math.PI) / 180;
  const dp = r(lat2 - lat1), dl = r(lng2 - lng1);
  const h = Math.sin(dp / 2) ** 2 + Math.cos(r(lat1)) * Math.cos(r(lat2)) * Math.sin(dl / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(h));
}

/**
 * Pente NON RENSEIGNÉE.
 *
 * Il n'y a plus de pente par défaut. Le 6/12 qui s'installait tout seul était
 * une supposition présentée comme une mesure : sur un toit à 10/12 elle enlève
 * 14 % de surface, sur un 13/12 un quart — et rien à l'écran ne disait que
 * personne ne l'avait vérifiée. Le couvreur choisit, ou le satellite mesure.
 *
 * `value: 0` traverse les calculs sans les casser (surface nulle, donc visible)
 * et l'enregistrement est bloqué tant qu'une pente manque.
 */
export const PITCH_VIDE = { rise: null, label: "", value: 0 };
export const pitchDefinie = (p) => Boolean(p) && p.rise !== null && p.value > 0;

/** Pente la plus courante en résidentiel — proposée, jamais imposée. */
export const PITCH_DEFAULT = PITCH_OPTIONS.find((p) => p.rise === 6);

export function pitchFromEstimate(est) {
  // Rien de lisible en entrée : on ne remplit plus le trou avec du 6/12.
  if (!est) return PITCH_VIDE;
  const s = String(est).toLowerCase();
  if (s.includes("flat") || s.includes("0/12")) return PITCH_OPTIONS[0];
  const m = s.match(/(\d+)\s*\/\s*12/);
  if (!m) return PITCH_VIDE;
  const n = parseInt(m[1], 10);
  const exact = PITCH_OPTIONS.find((p) => p.rise === n);
  if (exact) return exact;
  // Au-delà de 12/12 (le satellite va jusqu'à 18/12), on prend la pente la plus
  // proche — donc la plus forte — et non 6/12. Se tromper vers le haut sur un
  // toit manifestement raide coûte une soumission ; se tromper vers le bas
  // coûte le chantier.
  return PITCH_OPTIONS.reduce((a, b) =>
    Math.abs(b.rise - n) < Math.abs(a.rise - n) ? b : a);
}

/**
 * Paire de langues de travail d'un compte, déterminée par son pays :
 * un couvreur canadien sert des clients en français et en anglais,
 * un couvreur américain des clients en anglais et en espagnol.
 * Tout ce qui est bilingue dans l'interface (catalogue, relances,
 * langue des soumissions) se limite à cette paire.
 */
export function paireLangues(country) {
  return (country || "").toLowerCase() === "us" ? ["en", "es"] : ["fr", "en"];
}

/**
 * Libellé bilingue d'un poste du catalogue.
 *
 * `base` est l'ancien champ unilingue (name / nom / label), conservé comme
 * repli : un catalogue saisi avant le bilinguisme continue de s'afficher.
 * On retombe toujours sur une autre langue plutôt que sur du vide — une ligne
 * sans nom sur une soumission serait pire qu'une ligne dans la mauvaise langue.
 */
export function libelleBilingue(item, base, lang) {
  if (!item) return "";
  const en = (item[`${base}_en`] || "").trim();
  const fr = (item[`${base}_fr`] || "").trim();
  const es = (item[`${base}_es`] || "").trim();
  const vieux = (item[base] || "").trim();
  // Ordre de repli par langue : d'abord la langue voisine de la même paire
  // de travail, puis les autres, puis l'ancien champ unilingue.
  if (lang === "es") return es || en || fr || vieux;
  if (lang === "en") return en || fr || es || vieux;
  return fr || en || es || vieux;
}

export const libelleMateriau = (m, lang) => libelleBilingue(m, "name", lang);
export const libelleGroupe = (g, lang) => libelleBilingue(g, "nom", lang);
export const libelleOption = (o, lang) => libelleBilingue(o, "label", lang);

/** Le libellé stocké correspond-il à ce poste, quelle que soit la langue ? */
export function memeLibelle(item, base, valeur) {
  const v = (valeur || "").trim();
  if (!v) return false;
  return [item?.[`${base}_fr`], item?.[`${base}_en`], item?.[`${base}_es`], item?.[base]]
    .some((x) => (x || "").trim() === v);
}

/** Libellé d'un choix retenu, tel qu'il apparaît sur la soumission et le PDF. */
export function libelleChoix(ch) {
  if (!ch || !ch.produit) return ch?.groupe || "";
  const base = `${ch.groupe} — ${ch.produit}`;
  return ch.couleur ? `${base} (${ch.couleur})` : base;
}

/**
 * Un poste entre-t-il dans le calcul pour ce type de toit ?
 * Valeur absente = « tous » : les comptes configurés avant l'arrivée du toit
 * plat gardent exactement les mêmes prix qu'avant.
 */
export function applicableAuToit(item, plat) {
  const t = item?.toit || "tous";
  return t === "tous" || t === (plat ? "plat" : "pente");
}

/** Un toit est plat quand la pente n'ajoute aucune surface (0/12). */
// Pente inconnue (0) : surtout PAS « plat ». Un toit plat se chiffre en
// membrane, avec d'autres taux et d'autres produits — le supposer parce que la
// pente n'est pas encore saisie mettrait le mauvais catalogue sous les yeux.
export const estToitPlat = (pitchMultiplier) =>
  Number(pitchMultiplier) > 0 && Number(pitchMultiplier) <= 1.001;

/**
 * Lignes d'un SEUL bâtiment, avec son propre type de toit.
 *
 * Un toit plat n'est pas un toit en pente moins cher : membrane en rouleaux au
 * lieu de bardeau découpé, donc bien moins de pertes, une autre main-d'œuvre et
 * d'autres postes de matériaux. Tant qu'un chantier n'avait qu'un bâtiment, un
 * seul jeu de prix suffisait. Dès qu'un cabanon à membrane accompagne une
 * maison en bardeau, il faut chiffrer chacun avec ses propres taux.
 */
function lignesDunBatiment({ roofAreaSqft, settings, choix, plat, lang }) {
  const totalSquares = (roofAreaSqft || 0) / 100;
  // Un taux « toit plat » laissé à zéro veut dire « garder le taux principal ».
  const wastePct = plat
    ? Number(settings.flat_waste_percent) || 0
    : Number(settings.waste_percent) || 0;
  const laborRate = plat
    ? (Number(settings.flat_labor_rate_per_square) || Number(settings.labor_rate_per_square) || 0)
    : Number(settings.labor_rate_per_square) || 0;
  const laborCostRate = plat
    ? (Number(settings.flat_labor_cost_per_square) || Number(settings.labor_cost_per_square) || 0)
    : Number(settings.labor_cost_per_square) || 0;
  const adjustedSquares = totalSquares * (1 + wastePct / 100);

  // Les produits choisis pour la propriété passent en premier : c'est la ligne
  // que le client regarde. Un produit marqué « pente » ne s'applique pas au
  // cabanon plat, et inversement ; sans marquage il vaut pour tout, ce qui
  // garde le comportement des catalogues existants.
  const lignes = (choix || [])
    .filter((c) => c && c.produit && applicableAuToit(c, plat))
    .map((c) => ({
      name: libelleChoix(c),
      amount: (Number(c.price_per_square) || 0) * adjustedSquares,
      cost: (Number(c.cost_per_square) || 0) * adjustedSquares,
    }))
    .concat(
      (settings.materials || [])
        .filter((m) => applicableAuToit(m, plat))
        .map((m) => ({
          // Le libellé suit la langue de la SOUMISSION, pas celle de
          // l'interface : un couvreur francophone prépare des devis en anglais.
          name: libelleMateriau(m, lang),
          amount: (Number(m.price_per_square) || 0) * adjustedSquares,
          cost: (Number(m.cost_per_square) || 0) * adjustedSquares,
        }))
    );

  return {
    totalSquares, adjustedSquares, lignes,
    laborCost: laborRate * totalSquares,
    laborCoutant: laborCostRate * totalSquares,
  };
}

/**
 * Chiffrage d'un chantier de un ou plusieurs bâtiments.
 *
 * Chaque bâtiment produit ses lignes avec SON type de toit ; on les additionne
 * ensuite par libellé — un poste commun aux deux (le larmier, les clous) donne
 * une seule ligne au client, pas une par bâtiment.
 *
 * Un bâtiment porteur d'un `forfait` sort du calcul au carré et devient une
 * ligne unique à son prix. `roofArea` ne le compte donc pas : la surface
 * affichée reste celle qui a réellement servi à chiffrer, sans quoi diviser le
 * total par les pieds carrés donnerait un prix au carré qui ne veut rien dire.
 *
 * Le profit et la taxe, eux, se calculent UNE FOIS sur l'ensemble : une
 * soumission a une marge et une taxe, pas une par bâtiment.
 */
export function computeEstimateBatiments({ batiments = [], settings, choix = [], lang = "en" }) {
  const parNom = new Map();
  let laborCost = 0, laborCoutant = 0, totalSquares = 0, adjustedSquares = 0, roofArea = 0;

  // Bâtiments au forfait : un cabanon trop petit ou trop caché pour être mesuré
  // au satellite, dont le couvreur donne simplement les dimensions et un prix
  // rond. Il ne passe PAS par les carrés — sinon on lui appliquerait un bardeau,
  // une membrane, un pourcentage de pertes et un taux horaire pour un ouvrage
  // qu'il a chiffré de tête. Il devient une ligne à part sur la soumission.
  const forfaits = batiments.filter((b) => Number(b.forfait) > 0);
  const auCarre = batiments.filter((b) => !(Number(b.forfait) > 0));

  auCarre.forEach((b) => {
    const r = lignesDunBatiment({ roofAreaSqft: b.roofAreaSqft, settings, choix, plat: !!b.plat, lang });
    roofArea += b.roofAreaSqft || 0;
    totalSquares += r.totalSquares;
    adjustedSquares += r.adjustedSquares;
    laborCost += r.laborCost;
    laborCoutant += r.laborCoutant;
    r.lignes.forEach((li) => {
      const vu = parNom.get(li.name);
      if (vu) { vu.amount += li.amount; vu.cost += li.cost; }
      else parNom.set(li.name, { name: li.name, amount: li.amount, cost: li.cost });
    });
  });

  // Les forfaits arrivent APRÈS les postes au carré : sur le devis, le client
  // lit d'abord le gros œuvre, puis les extras.
  forfaits.forEach((b) => {
    parNom.set(`forfait:${b.label}`, {
      name: b.label,
      amount: Number(b.forfait) || 0,
      cost: Number(b.coutForfait) || 0,
    });
  });

  let lineItems = [...parNom.values()];
  let materialsCost = lineItems.reduce((a, b) => a + b.amount, 0);
  const materialsCoutant = lineItems.reduce((a, b) => a + (b.cost || 0), 0);
  const costTotal = materialsCoutant + laborCoutant;

  const pricingMode = settings.pricing_mode === "cout_profit" ? "cout_profit" : "vente";
  let subtotal;
  let profit;
  if (pricingMode === "cout_profit") {
    // La soumission part du coûtant et ajoute le profit ($ fixe ou %).
    profit = settings.profit_type === "montant"
      ? Number(settings.profit_value) || 0
      : (costTotal * (Number(settings.profit_value) || 0)) / 100;
    subtotal = costTotal + profit;
    // Le détail montré au client répartit le profit au prorata des coûtants :
    // les lignes somment juste, et les coûtants bruts ne sont jamais exposés.
    const facteur = costTotal > 0 ? subtotal / costTotal : 1;
    lineItems = lineItems.map((li) => ({ ...li, amount: (li.cost || 0) * facteur }));
    materialsCost = materialsCoutant * facteur;
    laborCost = laborCoutant * facteur;
    if (costTotal <= 0) {
      // Aucun coûtant saisi : tout le forfait passe en main-d'œuvre pour rester cohérent.
      laborCost = subtotal;
    }
  } else {
    subtotal = materialsCost + laborCost;
    // Profit affiché au couvreur seulement si des coûtants sont saisis.
    profit = costTotal > 0 ? subtotal - costTotal : null;
  }

  const tax = subtotal * ((Number(settings.tax_percent) || 0) / 100);
  const total = subtotal + tax;
  return {
    roofArea, totalSquares, adjustedSquares, lineItems,
    materialsCost, laborCost, subtotal, tax, total, costTotal, profit, pricingMode,
  };
}

/** Chiffrage d'un bâtiment unique — enveloppe conservée pour les appels existants. */
export function computeEstimate({ roofAreaSqft, footprintSqft, pitchMultiplier, settings, choix = [], plat = false, lang = "en" }) {
  const roofArea = roofAreaSqft != null ? roofAreaSqft : (footprintSqft || 0) * (pitchMultiplier || 1);
  return computeEstimateBatiments({ batiments: [{ roofAreaSqft: roofArea, plat }], settings, choix, lang });
}

export function polygonAreaFraction(poly) {
  if (!poly || poly.length < 3) return 0;
  let a = 0;
  for (let i = 0; i < poly.length; i++) {
    const [x1, y1] = poly[i];
    const [x2, y2] = poly[(i + 1) % poly.length];
    a += x1 * y2 - x2 * y1;
  }
  return Math.abs(a) / 2;
}

export function footprintFromPolygon(poly, groundW, groundH) {
  return polygonAreaFraction(poly) * (groundW || 0) * (groundH || 0) * 10.7639;
}

export const money = (n) =>
  `$${(Number(n) || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

/**
 * Vrai dès qu'AU MOINS un montant est saisi dans les réglages.
 *
 * Un compte neuf arrive avec les postes nommés mais tous les montants à zéro
 * (voir `default_settings()` côté serveur) : sans cette vérification, une
 * soumission sortirait à 0 $ sans que personne ne comprenne pourquoi. Sert à
 * afficher l'avertissement, pas à bloquer — un couvreur qui ne remplit que sa
 * main-d'œuvre sait ce qu'il fait.
 */
export function prixConfigures(s) {
  if (!s) return true; // réglages pas encore chargés : pas d'alarme prématurée
  const n = (v) => Number(v) || 0;
  const total =
    n(s.labor_rate_per_square) + n(s.labor_cost_per_square) +
    n(s.flat_labor_rate_per_square) + n(s.flat_labor_cost_per_square) +
    (s.materials || []).reduce((a, m) => a + n(m.price_per_square) + n(m.cost_per_square), 0) +
    (s.choix || []).reduce(
      (a, g) => a + (g.options || []).reduce(
        (b, o) => b + n(o.price_per_square) + n(o.cost_per_square), 0), 0);
  return total > 0;
}
